import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HeatmapModule from 'highcharts/modules/heatmap';
import { getChartMarginRight } from './printChartOptions';
import { DiaperIcon } from '@/components/svg/diaperIcon';
import { ToiletIcon } from '@/components/svg/toiletIcon';
import { t } from 'i18next';

HeatmapModule(Highcharts);

const diaperSvgData = DiaperIcon({ fillColor: '#fab1bf' });
const toiletSvgData = ToiletIcon({ fillColor: '#ffc000' });

interface Props {
  detectUrinationHeatmap: DetectUrinationHeatmapProps;
  isPrinting?: boolean;
}

interface DetectUrinationHeatmapProps {
  data: Record<string, boolean[]> | null;
  xCategories: string[];
  yCategories: string[];
}

// 排尿＆尿の溜まり具合Heatmap生成
export const DetectUrinationHeatmapGraph = React.memo(({ detectUrinationHeatmap, isPrinting = false }: Props) => {
  const urinationFlags = detectUrinationHeatmap.data;

  if (urinationFlags == null || Object.keys(urinationFlags).length === 0) {
    return null;
  }

  const xAxisCategories = detectUrinationHeatmap.xCategories ? detectUrinationHeatmap.xCategories : [];
  const xAxisMax = detectUrinationHeatmap.xCategories ? detectUrinationHeatmap.xCategories.length - 1 : 0;

  type UrinationPoint = [number, number, number];
  type UrinationHeatmap = UrinationPoint[];
  const urinationHeatmap: UrinationHeatmap = urinationFlags.urination.map((flag, x) => {
    let value = 0;
    if (urinationFlags.poolUrine[x]) {
      // poolUrineが優先
      value = 2;
    } else if (flag) {
      value = 1;
    }
    return [x, 0, value];
  });

  const option: Highcharts.Options = {
    title: {
      text: '',
    },
    chart: {
      zoomType: 'x',
      height: 30,
      marginLeft: 120,
      marginRight: getChartMarginRight(!!isPrinting),
      marginTop: 0,
      marginBottom: 0,
      shadow: false,
      animation: false,
      backgroundColor: '#ffffff',
    },
    xAxis: {
      categories: xAxisCategories,
      min: 0,
      max: xAxisMax,
      tickInterval: 2,
      visible: false,
    },
    yAxis: {
      categories: [`${t('', '')}`],
      reversed: false,
      gridLineWidth: 0,
      title: { text: '' },
      labels: {
        style: {
          fontSize: '14px',
          fontWeight: 'bold',
        },
      },
    },
    series: [
      {
        type: 'scatter',
        boostThreshold: 100,
        data: urinationHeatmap
          .filter((point) => point[2] !== 0)
          .map((point) => ({
            x: point[0],
            y: point[1],
            value: point[2],

            marker: {
              symbol:
                point[2] === 1
                  ? `url(${diaperSvgData})` // おむつ交換の目安
                  : `url(${toiletSvgData})`, // トイレ誘導の目安
              width: 20,
              height: 20,
            },
          })),
        index: 0,
      },
    ],

    tooltip: {
      headerFormat: '',
      pointFormatter: function () {
        const tooltipTexts = {
          1: t('estimatedDiaperUse', 'おむつ交換の目安'),
          2: t('estimatedToiletGuidance', 'トイレ誘導の目安'),
        };
        return tooltipTexts[this.value as keyof typeof tooltipTexts] || '';
      },
    },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    boost: {
      useGPUTranslations: true,
    },
  };

    React.useEffect(() => {
        Highcharts.charts.forEach((chart) => {
            if (chart) {
                chart.reflow();
            }
        });
    }, [isPrinting]);

    return <HighchartsReact highcharts={Highcharts} options={option} />;
});

